import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";
import { extractDomain } from "../../utils/helpers";
import { baseQueryWithAuthCheck } from "../../utils/baseQueryWithAuthCheck";
import { endPoints, MIDDLEWARE_SLUG } from "../../apis/apiEndPoints";
import {
  IFolderDetails,
  IJobTitleSuggestions,
  ISaveAndContinueResponse,
} from "../../types/campaign.types";

const DOMAIN = extractDomain();

export const campaignSetupApi = createApi({
  reducerPath: "campaignSetupApi",
  baseQuery: baseQueryWithAuthCheck(
    fetchBaseQuery({
      baseUrl: `${window.location.origin}${MIDDLEWARE_SLUG}/middleware`,
    })
  ),
  endpoints: (builder) => ({
    // to fetch job title suggestions - useGetJobTitleSuggestionsMutation
    getJobTitleSuggestions: builder.mutation<
      IJobTitleSuggestions[],
      { jobTitle: string, random: number }
    >({
      query: ({ jobTitle, random }) => ({
        url: `${endPoints.JOB_LIST_V2}?q=${jobTitle || ""}&random=${random}&type=kwd`,
        method: "GET",
        headers: {
          domain: DOMAIN,
        },
        withCredentials: true,
      }),
      transformResponse: (data: any) =>{
        const dataArr: string[] = data.split("|");
        return dataArr?.map(item => {
          const jobTitle = item.split("~")[0];
          return {label: jobTitle, value: jobTitle}}
        ) || []
      }
    }),

    // to fetch folders list mutation - useGetFoldersListMutation
    getFoldersList: builder.mutation<IFolderDetails[], { folderName: string }>({
      query: ({ folderName }) => ({
        url: `${endPoints.FOLDER_LIST}`,
        method: "POST",
        headers: {
          domain: DOMAIN,
        },
        withCredentials: true,
        body: {
          query: folderName || ""
        }
      }),
    }),

    // to fetch folders list query - useGetFoldersQuery
    getFolders: builder.query<IFolderDetails[], {}>({
      query: () => ({
        url: `${endPoints.FOLDER_LIST}`,
        method: "POST",
        headers: {
          domain: DOMAIN,
        },
        withCredentials: true,
        body: {
          query: ""
        }
      }),
    }),

    // create folder - usePostCreateFolderMutation
    postCreateFolder: builder.mutation<
      unknown,
      {
        channelId: string;
        corpId: string;
        folderName: string;
        subChannelId: string;
        subUid: string;
      }
    >({
      query: (payload) => ({
        url: `${endPoints.PERSONAL_FOLDER}`,
        method: "POST",
        headers: {
          domain: DOMAIN,
        },
        body: payload,
        withCredentials: true,
      }),
    }),

    // save as draft - usePostSaveAsDraftMutation
    postSaveAsDraft: builder.mutation<
      unknown,
      {
        payload: FormData;
      }
    >({
      query: ({ payload }) => ({
        url: `${endPoints.START_CAMPAIGN}`,
        method: "POST",
        body: payload,
        headers: {
          domain: DOMAIN,
        },
        withCredentials: true,
      }),
    }),

    // save and continue - usePostSaveAndContinueMutation
    postSaveAndContinue: builder.mutation<
      ISaveAndContinueResponse,
      { payload: FormData }
    >({
      query: ({ payload }) => ({
        url: `${endPoints.START_CAMPAIGN}`,
        method: "POST",
        headers: {
          domain: DOMAIN,
        },
        body: payload,
        withCredentials: true,
      }),
    }),
  }),
});

export const {
  useGetJobTitleSuggestionsMutation,
  useGetFoldersListMutation,
  usePostSaveAsDraftMutation,
  usePostSaveAndContinueMutation,
  usePostCreateFolderMutation,
  useGetFoldersQuery
} = campaignSetupApi;
